import {
  type PropsWithChildren,
  type ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { DeploymentRegion } from '@nest-react/domain';

import {
  type RegionalApiClient,
  createRegionalApiClient,
  setActiveRegionalApiClient,
} from './regionalApiClient';

const LAST_REGION_KEY = 'mylo-studio:last-region';

export type RegionState =
  'unselected' | 'verifying' | 'ready' | 'switching' | 'failed';
export type RegionEndpoints = Record<DeploymentRegion, string>;
type RegionEndpointConfig = Partial<RegionEndpoints>;

interface RegionContextValue {
  region: DeploymentRegion | null;
  state: RegionState;
  error: string | null;
  client: RegionalApiClient | null;
  selectRegion: (region: DeploymentRegion) => void;
  resetRegion: () => Promise<void>;
  retry: () => void;
}

const RegionContext = createContext<RegionContextValue | null>(null);

const getSavedRegion = (): DeploymentRegion | null => {
  const routedRegion = window.location.pathname.match(
    /^\/(?:join|auth\/region-callback)\/(RU|GLOBAL)(?:\/|$)/
  )?.[1];
  if (routedRegion === 'RU' || routedRegion === 'GLOBAL') return routedRegion;
  return 'RU';
};

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);

export const resolveRegionEndpoints = (
  configured: RegionEndpointConfig,
  hostname: string
): RegionEndpoints => {
  if (configured.RU && configured.GLOBAL) {
    return { RU: configured.RU, GLOBAL: configured.GLOBAL };
  }
  if (LOCAL_HOSTS.has(hostname)) {
    return {
      RU: configured.RU || 'http://localhost:4000',
      GLOBAL: configured.GLOBAL || 'http://localhost:4002',
    };
  }
  const missing = [
    !configured.RU ? 'VITE_RU_API_URL' : '',
    !configured.GLOBAL ? 'VITE_GLOBAL_API_URL' : '',
  ].filter(Boolean);
  throw new Error(`Missing Region API configuration: ${missing.join(', ')}`);
};

export const configuredRegionEndpoints = (): RegionEndpoints =>
  resolveRegionEndpoints(
    {
      RU: import.meta.env.VITE_RU_API_URL,
      GLOBAL: import.meta.env.VITE_GLOBAL_API_URL,
    },
    window.location.hostname
  );

const DEFAULT_REGION_ENDPOINTS = configuredRegionEndpoints();

export const RegionProvider = ({
  children,
  endpoints = DEFAULT_REGION_ENDPOINTS,
}: PropsWithChildren<{ endpoints?: RegionEndpoints }>): ReactElement => {
  const [initialRegion] = useState(getSavedRegion);
  const [region, setRegion] = useState<DeploymentRegion | null>(initialRegion);
  const [state, setState] = useState<RegionState>(
    initialRegion ? 'verifying' : 'unselected'
  );
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const clientRef = useRef<RegionalApiClient | null>(null);
  const forceFreshLoginRef = useRef(false);
  const [client, setClient] = useState<RegionalApiClient | null>(null);

  useEffect(() => {
    if (!region) return undefined;
    let current = true;
    const controller = new AbortController();
    void fetch(`${endpoints[region].replace(/\/$/, '')}/status/region`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async response => {
        if (!response.ok)
          throw new Error(`Region check failed (${response.status})`);
        const payload = (await response.json()) as { region?: string };
        if (payload.region !== region)
          throw new Error('Region endpoint mismatch');
        if (!current) return;
        if (forceFreshLoginRef.current) {
          const logout = await fetch(
            `${endpoints[region].replace(/\/$/, '')}/auth/logout`,
            {
              method: 'POST',
              credentials: 'include',
              signal: controller.signal,
            }
          );
          if (!logout.ok)
            throw new Error(`Target Region logout failed (${logout.status})`);
          forceFreshLoginRef.current = false;
        }
        clientRef.current?.deactivate();
        const next = createRegionalApiClient(endpoints[region]);
        clientRef.current = next;
        setActiveRegionalApiClient(next);
        setClient(next);
        localStorage.setItem(LAST_REGION_KEY, region);
        setState('ready');
      })
      .catch(reason => {
        if (!current || controller.signal.aborted) return;
        clientRef.current?.deactivate();
        clientRef.current = null;
        setActiveRegionalApiClient(null);
        setClient(null);
        setError(
          reason instanceof Error ? reason.message : 'Region check failed'
        );
        setState('failed');
      });
    return () => {
      current = false;
      controller.abort();
    };
  }, [attempt, endpoints, region]);

  const selectRegion = useCallback((next: DeploymentRegion) => {
    clientRef.current?.deactivate();
    clientRef.current = null;
    setActiveRegionalApiClient(null);
    setClient(null);
    forceFreshLoginRef.current = true;
    setRegion(next);
    setState('verifying');
  }, []);
  const resetRegion = useCallback(async () => {
    const currentClient = clientRef.current;
    if (currentClient) {
      await currentClient
        .request('/auth/logout', { method: 'POST' })
        .catch(() => undefined);
    }
    currentClient?.deactivate();
    clientRef.current = null;
    setActiveRegionalApiClient(null);
    setClient(null);
    localStorage.setItem(LAST_REGION_KEY, 'RU');
    setRegion('RU');
    setState('verifying');
    setError(null);
    setAttempt(value => value + 1);
  }, []);
  const retry = useCallback(() => {
    setState('verifying');
    setError(null);
    setAttempt(value => value + 1);
  }, []);
  const value = useMemo(
    () => ({ region, state, error, client, selectRegion, resetRegion, retry }),
    [client, error, region, resetRegion, retry, selectRegion, state]
  );
  return (
    <RegionContext.Provider value={value}>{children}</RegionContext.Provider>
  );
};

export const useRegion = (): RegionContextValue => {
  const value = useContext(RegionContext);
  if (!value) throw new Error('RegionProvider is missing');
  return value;
};

export const useRegionalApi = (): RegionalApiClient => {
  const { client, state } = useRegion();
  if (state !== 'ready' || !client)
    throw new Error('Regional API is not ready');
  return client;
};
